import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * The privacy policy and the terms, as pages of the app.
 *
 * Google requires both to be reachable **without signing in**, on the same
 * domain as the homepage, and linked from it - so they sit outside
 * `ProtectedRoute` and the public homepage carries the links. A policy behind a
 * login is a policy the reviewer cannot read.
 *
 * They are one file because they share a frame and are edited together: the
 * two documents disagree the moment one is updated and the other is not.
 *
 * The text is written from what the code actually does. When that changes -
 * a new scope, a new third party, a new thing stored - this file changes in
 * the same commit, or it becomes a false statement rather than a stale one.
 */

/**
 * Shown on both pages and on Google's consent screen as the support address.
 * One constant, because the two must not drift.
 */
const CONTACT_EMAIL = 'phabry@gmail.com';

/** The date the wording last changed. Update it when the wording does. */
const LAST_UPDATED = '21 August 2026';

function LegalPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="legal-page">
      <article className="legal-page__sheet">
        <nav className="legal-page__nav">
          <Link to="/login">← LiveForge</Link>
        </nav>
        <h1>{title}</h1>
        <p className="legal-page__updated muted">Last updated: {LAST_UPDATED}</p>
        {children}
        <footer className="legal-page__footer muted">
          <Link to="/privacy">Privacy Policy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms">Terms of Service</Link>
        </footer>
      </article>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        LiveForge is a self-hosted live streaming studio. This instance is operated by a private
        individual, reachable at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>, and runs
        on infrastructure the operator controls. This policy describes what it stores, why, and for
        how long.
      </p>

      <h2>Who this applies to</h2>
      <p>
        Two kinds of people use LiveForge. <strong>Hosts</strong> sign in and produce broadcasts.{' '}
        <strong>Guests</strong> do not sign in at all — they join a single broadcast through an
        invite link and are asked only for a display name.
      </p>

      <h2>What is collected</h2>
      <h3>Account data (hosts)</h3>
      <p>
        Sign-in is handled by Firebase Authentication. When you sign in, LiveForge stores your email
        address, your display name, your profile picture URL and the identifier Firebase assigns
        you. Passwords, when you use one, are held by Firebase and are never seen by LiveForge.
      </p>

      <h3>Content you create</h3>
      <p>
        Shows and broadcasts, with their titles, descriptions, schedules, layouts and branding; the
        names of guests you invite and the random tokens their invite links carry.
      </p>

      <h3>Audio and video</h3>
      <p>
        Camera, microphone and screen share travel through a LiveKit server the operator runs. They
        are not stored unless a broadcast is recorded, in which case the resulting file is written
        to the operator&apos;s own storage and is downloadable only by the host who owns that
        broadcast.
      </p>

      <h3>Google user data</h3>
      <p>
        Only if you choose to connect a YouTube channel to a Show. LiveForge then stores, for that
        Show:
      </p>
      <ul>
        <li>
          the channel&apos;s identifier, title, handle and thumbnail URL, so the app can show you
          which channel is connected;
        </li>
        <li>
          an OAuth <strong>refresh token</strong> and a short-lived access token, which are what let
          LiveForge act on that channel on your behalf.
        </li>
      </ul>
      <p>
        The tokens are encrypted with AES-256-GCM before being written to the database, and are
        never sent to a browser — no page of this app, and no API response, contains them.
      </p>

      <h2>How Google user data is used</h2>
      <p>
        LiveForge requests two scopes, and uses them for one purpose each, both of which are
        features you can see in the app:
      </p>
      <ul>
        <li>
          <code>auth/youtube</code> — to read the channel you connected so it can be displayed, and
          to create and manage the live broadcasts you start from LiveForge, including retrieving
          the stream key the broadcast needs.
        </li>
        <li>
          <code>auth/youtube.force-ssl</code> — to read the live chat of your own broadcast, so
          messages can be shown to you and put on screen when you select them.
        </li>
      </ul>
      <p>
        <strong>Limited Use.</strong> LiveForge&apos;s use of information received from Google APIs
        adheres to the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Google user data is used only to provide the
        features described above; it is not transferred to anyone else except as needed to provide
        those features, is never sold, is never used for advertising, and is never used to train
        generalised artificial intelligence or machine learning models. No human reads it, except
        where you have given explicit consent for a specific issue, where it is required by law, or
        where it is necessary for security purposes such as investigating abuse.
      </p>

      <h2>Who else sees any of this</h2>
      <p>
        LiveForge is self-hosted and does not use analytics, advertising or tracking of any kind.
        Data leaves the operator&apos;s infrastructure in exactly three directions:
      </p>
      <ul>
        <li>
          <strong>Google (Firebase Authentication)</strong> — to sign you in.
        </li>
        <li>
          <strong>Google (YouTube Data API)</strong> — only for a Show with a connected channel, and
          only to do what you asked.
        </li>
        <li>
          <strong>YouTube</strong> — the broadcast itself, when you go live, which is the point of
          the app.
        </li>
      </ul>
      <p>Nothing is sold, rented or shared with anyone else.</p>

      <h2>Keeping and deleting</h2>
      <p>
        Content is kept until you delete it. Deleting a Show deletes its broadcasts and their guest
        invites with it.
      </p>
      <p>
        <strong>Disconnecting a YouTube channel</strong> — the Disconnect button on the Show
        settings screen — asks Google to revoke the authorisation and deletes the stored tokens and
        channel details immediately. You can also revoke access at any time from{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
          your Google Account permissions
        </a>
        , without going through LiveForge at all.
      </p>
      <p>
        To have your account and everything in it deleted, write to{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>Security</h2>
      <p>
        All traffic is served over HTTPS. OAuth tokens are encrypted at rest. The database is not
        reachable from the public internet. No system is perfect, and this one is operated by one
        person — it is offered honestly, not as a guarantee.
      </p>

      <h2>Changes</h2>
      <p>
        If this policy changes, the date at the top changes with it. Material changes affecting how
        Google user data is handled will be notified to connected accounts by email.
      </p>
    </LegalPage>
  );
}

export function TermsPage() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        LiveForge is a self-hosted live streaming studio operated by a private individual, reachable
        at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. By using this instance you
        accept the terms below.
      </p>

      <h2>The service</h2>
      <p>
        LiveForge lets you produce a live broadcast from a browser: bring in cameras and guests,
        compose them, and stream or record the result. It is a personal project, provided free of
        charge, with no service level of any kind.
      </p>

      <h2>Your account</h2>
      <p>
        You are responsible for what happens under your account and for keeping your sign-in
        credentials to yourself. Accounts are for the people the operator has given access to; do
        not share yours.
      </p>

      <h2>Acceptable use</h2>
      <p>Do not use LiveForge to:</p>
      <ul>
        <li>broadcast or record anyone without their knowledge and agreement;</li>
        <li>publish content that is unlawful where you or your audience are;</li>
        <li>infringe anyone&apos;s copyright, including in music and video you bring in;</li>
        <li>attack, overload or attempt to gain unauthorised access to the service.</li>
      </ul>

      <h2>YouTube</h2>
      <p>
        When you connect a YouTube channel, you are also bound by the{' '}
        <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">
          YouTube Terms of Service
        </a>{' '}
        and the{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
          Google Privacy Policy
        </a>
        . What you broadcast is published on your own channel, under your own account, and is
        subject to YouTube&apos;s rules and enforcement — not the operator&apos;s.
      </p>

      <h2>Your content</h2>
      <p>
        What you create and broadcast remains yours. The operator claims no rights over it and
        stores it only to run the service for you.
      </p>

      <h2>No warranty</h2>
      <p>
        LiveForge is provided &ldquo;as is&rdquo;. It may be unavailable, lose a recording, or fail
        in the middle of a broadcast. Do not use it for anything you cannot afford to have go wrong,
        and keep your own recording where an occasion matters. To the extent the law allows, the
        operator is not liable for any loss arising from its use.
      </p>

      <h2>Ending</h2>
      <p>
        You may stop using LiveForge at any time and ask for your data to be deleted. The operator
        may suspend or remove an account that breaches these terms, or shut the service down
        entirely, with reasonable notice where circumstances allow.
      </p>

      <h2>Changes</h2>
      <p>
        These terms may change; the date at the top will say when they last did. Continuing to use
        the service after a change means accepting it.
      </p>
    </LegalPage>
  );
}
